import { ApiError } from '@/services/api'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Typography } from '@/shared/components/ui/typography'
import { useUserContext } from '@/shared/contexts/userContext'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { cn } from '@/shared/lib/utils'
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SETTINGS_SECTIONS } from '../constants/settings.constants'
import { SettingsSection } from '../types/settings.types'
import { SettingsSidebar } from './SettingsSidebar'

const WorkspaceSettings = lazy(() => import('./WorkspaceSettings'))
const PlansAndBilling = lazy(() => import('./PlansAndBilling'))
const AccountProfile = lazy(() => import('./AccountProfile'))
const PeopleSettings = lazy(() => import('./PeopleSettings'))
const NotificationsSettings = lazy(() => import('./NotificationsSettings'))
const AiSettings = lazy(() => import('./AISettings'))

export const SettingsModal = () => {
  const { error: hasErrorLoadingUser, isLoading: isLoadingUser } =
    useUserContext()
  const location = useLocation()
  const navigate = useNavigate()
  const { workspaceId } = useWorkspaceContext()

  const { flags: [
    isNotificationsManagementEnabled,
    isWorkspacePeopleManagementEnabled,
    isWorkspaceSettingsEnabled,
    isWorkspacePlansEnabled,
  ] } = useFeatureFlagsWithWorkspace([
    'settingsNotificationsManagement',
    'settingsWorkspacePeopleManagement',
    'settingsWorkspace',
    'settingsWorkspacePlans',
  ])

  const searchParams = new URLSearchParams(location.search)
  const settingsParam = searchParams.get('settings')
  const isOpen = settingsParam !== null

  const [isSidebarOpen, setIsSidebarOpen] = useState(!settingsParam)
  const defaultPage = !workspaceId ? 'profile' : 'workspace'
  const activeSection: SettingsSection = settingsParam as SettingsSection

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.delete('settings')

    const newSearch = newSearchParams.toString()
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '')

    navigate(newUrl)
  }

  const handleSectionChange = (section: SettingsSection) => {
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('settings', section)

    const newUrl = location.pathname + '?' + newSearchParams.toString()
    navigate(newUrl)
  }

  const contentMap = useMemo(
    () =>
      new Map(
        Object.entries({
          ...(isWorkspaceSettingsEnabled && {
            [SETTINGS_SECTIONS.workspace.id]: WorkspaceSettings,
          }),
          ...(isWorkspacePlansEnabled && {
            [SETTINGS_SECTIONS.plans.id]: PlansAndBilling,
          }),
          [SETTINGS_SECTIONS.profile.id]: AccountProfile,
          [SETTINGS_SECTIONS.aiSettings.id]: AiSettings,
          ...(isWorkspacePeopleManagementEnabled && {
            [SETTINGS_SECTIONS.people.id]: PeopleSettings,
          }),
          ...(isNotificationsManagementEnabled && {
            [SETTINGS_SECTIONS.notifications.id]: NotificationsSettings,
          }),
        })
      ),
    [
      isWorkspaceSettingsEnabled,
      isWorkspacePlansEnabled,
      isWorkspacePeopleManagementEnabled,
      isNotificationsManagementEnabled,
    ]
  )

  const isCurrentSectionAvailable = contentMap.has(activeSection)

  useEffect(() => {
    if (!isOpen || isLoadingUser) {
      return
    }

    if (hasErrorLoadingUser) {
      throw new ApiError('Unauthorized', 401)
    }
  }, [hasErrorLoadingUser, isLoadingUser, isOpen])

  useEffect(() => {
    if (isOpen) {
      const originalTitle = document.title

      const title = SETTINGS_SECTIONS[activeSection]?.label || 'Settings'
      document.title = `${title} | Mydra`

      return () => {
        document.title = originalTitle
      }
    }
  }, [isOpen, activeSection])

  useEffect(() => {
    if (settingsParam) {
      setIsSidebarOpen(false)
    }
  }, [settingsParam])

  if (!isCurrentSectionAvailable && isOpen) {
    const availableSections = Array.from(contentMap.keys())

    if (availableSections.length > 0) {
      const fallbackSection = availableSections[0]
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.set('settings', fallbackSection)
      const newUrl = location.pathname + '?' + newSearchParams.toString()
      navigate(newUrl, { replace: true })
    } else {
      handleClose()
    }

    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="md:w-6xl flex h-full max-w-full flex-col gap-0 overflow-hidden rounded-none bg-grey-02 p-0 md:h-[90vh] md:max-w-[90vw] md:rounded-lg">
        <DialogTitle className="relative z-40 bg-white md:hidden">
          <div className="relative mx-3 flex items-center gap-2 border-b pb-4 pt-5">
            <button
              className={cn(isSidebarOpen ? 'hidden' : 'block')}
              onClick={() => setIsSidebarOpen((v) => !v)}
            >
              <ChevronLeftOutlined
                sx={{ fontSize: 20 }}
                className="-ml-2 text-gray-800"
              />
            </button>
            <Typography
              variant="body2"
              className={cn(
                'flex-1 font-semibold capitalize text-black',
                isSidebarOpen ? 'text-center' : 'text-left'
              )}
            >
              Settings
            </Typography>
          </div>
        </DialogTitle>
        <div className="relative flex flex-1 overflow-hidden">
          <SettingsSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            className={cn(
              'absolute z-10 h-full w-full bg-white md:relative md:w-64 md:bg-grey-02',
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
              'md:!translate-x-0'
            )}
          />
          <div className="relative flex-1 overflow-hidden bg-white">
            <Suspense fallback={<div>Loading...</div>}>
              {(() => {
                const Component = contentMap.get(activeSection ?? defaultPage)
                return Component ? <Component /> : null
              })()}
            </Suspense>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
